import { useTranslation } from 'react-i18next';
import { memo, type FC, type ReactNode } from 'react';
import BasePagination from 'components/atoms/base-pagination';
import { ELASTIC_SEARCH_MAX_ITEMS } from 'constants/count';
import { safeClamp } from 'utils/helpers/safeClamp';
import RpmOverviewTable from 'components/molecules/rpm-overview-table';
import RpmOverviewSlaSection from 'components/molecules/rpm-overview-sla-section';
import {
  useOverviewDashboardFilter,
  useRpmOverviewDashboard,
} from 'infrastructure/hooks/rpm/overview-dashboard';
import { useAssigneeFiltersCount } from 'infrastructure/hooks/rpm/overview-dashboard/use-assignee-filters-count';
import SortSelectBox from 'components/molecules/rpm-overview-table/components/sort-select-box';
import useSlaList from 'utils/hooks/rpmOverviewDashboard/useSlaList';
import useUser from 'utils/useUser';
import { Flex, Typography } from 'antd';
import BaseButton from 'components/atoms/baseButton';

import s from './styles.module.scss';
import { getAssigneeFilters } from './utils';

const { Text } = Typography;

export const PATIENTS_PER_PAGE = 30;

const RpmOverviewDashboard: FC = () => {
  const { t } = useTranslation();
  const { filter, updateFilter } = useOverviewDashboardFilter();
  const {
    isMedicalAssistantRole,
    hasRNRole,
    isAgencyDoctor,
    agencies,
    hasRpmPodAssignment,
  } = useUser();
  const agencyGuid = agencies.map((a) => a.guid);
  const { patients, isLoading, isFetching } = useRpmOverviewDashboard(
    filter,
    agencyGuid,
  );
  const {
    data: slaData,
    isLoading: slaLoading,
    isError: slaError,
  } = useSlaList(filter);
  const { filtersCount } = useAssigneeFiltersCount(filter);

  const assigneeFilters = getAssigneeFilters(t, filtersCount);

  const hideSortSelect =
    isAgencyDoctor && (isMedicalAssistantRole || hasRNRole);

  const isPodFiltered = filter.podFilter !== undefined;

  const podFilterEmptyText: ReactNode = isPodFiltered ? (
    <div data-cy="rpm-queue-empty-state" data-testid="rpm-queue-empty-state">
      <p>{t('labels.rpmPodFilterEmptyTitle')}</p>
      <p>{t('labels.rpmPodFilterEmptyBody')}</p>
    </div>
  ) : undefined;

  const wrapperId = 'rpm-overview-dashboard';

  return (
    <div className={s.wrapper} id={wrapperId}>
      <div className={s.header}>
        <Flex align="center" gap={16}>
          <Text className={s.patients}>{t('labels.patients')}</Text>
          {assigneeFilters.map((assigneeFilter) => {
            const isSelected = filter.assignee === assigneeFilter.key;

            const label = `${assigneeFilter.label} (${
              assigneeFilter.count ?? 0
            })`;

            return (
              <BaseButton
                key={assigneeFilter.key}
                label={label}
                type="primary"
                className={
                  isSelected
                    ? s['patient-filters']
                    : s['patient-filters-unselected']
                }
                onClick={() =>
                  updateFilter({
                    assignee: assigneeFilter.key,
                    page: undefined,
                  })
                }
              />
            );
          })}
        </Flex>
        {!hideSortSelect && (
          <SortSelectBox
            value={filter.sorting}
            onChange={(v) => updateFilter({ sorting: v, page: undefined })}
            allowClear
            withNone={false}
          />
        )}
      </div>
      {hasRpmPodAssignment ? (
        <>
          {/* SLA section — molecule handles skeleton/visible/hidden states internally */}
          <RpmOverviewSlaSection
            slaData={slaData}
            slaLoading={slaLoading}
            slaError={slaError}
            initialSkeletonClassName={s['initial-loading-skeleton']}
            startTime={
              filter.startTime
                ? new Date(filter.startTime).toISOString()
                : undefined
            }
            endTime={
              filter.endTime
                ? new Date(filter.endTime).toISOString()
                : undefined
            }
            emptyText={podFilterEmptyText}
          />
          {/* Pod-Prioritized Work section */}
          <>
            {!isLoading && (
              <div
                data-cy="section-header-pod-prioritized"
                data-testid="section-header-pod-prioritized"
              >
                <Text className={s['section-label']}>
                  {t('labels.pods.section.podPrioritized')}
                </Text>
              </div>
            )}
            <RpmOverviewTable
              patients={patients?.data}
              isInitialLoading={isLoading}
              loading={isLoading || isFetching}
              page={1}
              setPage={() => {}}
              pageSize={PATIENTS_PER_PAGE}
              wrapperId={wrapperId}
              startTime={
                filter.startTime
                  ? new Date(filter.startTime).toISOString()
                  : undefined
              }
              endTime={
                filter.endTime
                  ? new Date(filter.endTime).toISOString()
                  : undefined
              }
              initialSkeletonClassName={s['initial-loading-skeleton']}
              emptyText={podFilterEmptyText}
            />
          </>
        </>
      ) : (
        /* Standard flat list — pre-PODS experience, unchanged */
        <div>
          <RpmOverviewTable
            patients={patients?.data}
            isInitialLoading={isLoading}
            loading={isLoading || isFetching}
            page={1}
            setPage={() => {}}
            pageSize={PATIENTS_PER_PAGE}
            wrapperId={wrapperId}
            startTime={
              filter.startTime
                ? new Date(filter.startTime).toISOString()
                : undefined
            }
            endTime={
              filter.endTime
                ? new Date(filter.endTime).toISOString()
                : undefined
            }
            initialSkeletonClassName={s['initial-loading-skeleton']}
          />
        </div>
      )}
      {!isLoading && (
        <div className={s.pagination}>
          <BasePagination
            page={filter.page}
            total={safeClamp(patients?.count, 0, ELASTIC_SEARCH_MAX_ITEMS)}
            align="end"
            onChange={(newPage) => {
              updateFilter({ page: newPage });
            }}
            pageSize={PATIENTS_PER_PAGE}
            pageSizeOptions={[]}
            hide={false}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};
export default memo(RpmOverviewDashboard);
